import axios from 'axios';

// Determine API base URL based on environment
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    // Ensure REACT_APP_API_BASE_URL is set in your environment for the CLIENT service
    // and it should point to the full base URL of your API service, e.g., https://api.pointstuff.com/api
    return process.env.REACT_APP_API_BASE_URL || 'https://api.pointstuff.com/api';
  }
  
  // For local development (including Docker if REACT_APP_DOCKER is not specifically handled elsewhere for a different port)
  // Ensure REACT_APP_API_BASE_URL, if set for local dev,
  // points to your local backend including /api, e.g., http://localhost:5000/api
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
};

// Create an instance of axios with default config
const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  // Add timeout
  timeout: 30000,
  // Enable credentials for CORS
  withCredentials: true
});

/**
 * The API's origin, for links the browser follows itself rather than sends
 * through axios — an OAuth redirect, say. Derived from the same value axios uses
 * so it cannot disagree with it, unlike the separate REACT_APP_API_URL that some
 * older pages read and that is not always set.
 */
export const getApiRoot = () => getBaseUrl().replace(/\/api\/?$/, '');

export { api };

// Add additional headers for CORS if needed
api.defaults.headers.common['Accept'] = 'application/json';
// Ensure proper content type for requests
api.defaults.headers.post['Content-Type'] = 'application/json';

// Simple debounce function to prevent excessive API calls
const debounceMap = new Map();
const debounce = (key, fn, delay = 300) => {
  if (debounceMap.has(key)) {
    clearTimeout(debounceMap.get(key));
  }
  
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(async () => {
      debounceMap.delete(key);
      try {
        const result = await fn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }, delay);
    
    debounceMap.set(key, timeoutId);
  });
};

// Add request/response interceptors for debugging
api.interceptors.request.use(
  (config) => {
    // Retrieve the token from localStorage (or wherever AuthContext stores it)
    const token = localStorage.getItem('authToken'); // Corrected key to 'authToken'
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`, config.data, config.headers.Authorization ? 'Token Sent' : 'No Token');
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Response: ${response.status}`, response.data);
    }
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.message, error.response?.data);
    if (error.response && error.response.status === 401) {
      // Check if we are already on the login page to avoid redirect loops
      if (window.location.pathname !== '/login') {
        // Clear any stored auth tokens or user data
        localStorage.removeItem('authToken');
        // Optionally, clear other user-related data from localStorage or context
        // dispatch({ type: 'LOGOUT' }); // If using a global state management like Redux/Context for auth

        // Redirect to login page
        // Preserve the current location to redirect back after login, if desired
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to retry API calls with exponential backoff
const withRetry = async (apiCall, maxRetries = 3) => {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await apiCall();
    } catch (error) {
      console.log(`API call failed, retrying (${i + 1}/${maxRetries})...`);
      lastError = error;
      // Exponential backoff: wait 2^i * 500ms between retries
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 500));
    }
  }
  throw lastError;
};

// Server status check
export const checkServerStatus = async () => {
  try {
    const response = await axios.get(`${getBaseUrl().replace('/api', '')}/health`, { 
      timeout: 3000 
    });
    return {
      isOnline: true,
      message: response.data.message || 'Server is online'
    };
  } catch (error) {
    console.error('Server status check failed:', error);
    return {
      isOnline: false,
      message: 'Server is offline or unreachable'
    };
  }
};

// Session APIs
export const createSession = async (sessionData) => {
  const res = await withRetry(() => api.post('/sessions', sessionData));
  return res.data;
};

export const getSession = async (sessionId) => {
  // Use debounce for this frequently called function
  const key = `getSession-${sessionId}`;
  const res = await debounce(key, () => withRetry(() => api.get(`/sessions/${sessionId}`)));
  return res.data;
};

export const updateSession = async (sessionId, sessionData) => {
  const res = await api.put(`/sessions/${sessionId}`, sessionData);
  return res.data;
};

// Participant APIs
export const joinSession = async (sessionId, name, role = 'participant') => {
  const res = await withRetry(() => api.post('/participants', { sessionId, name, role }));
  return res.data;
};

export const updateParticipant = async (participantId, participantData) => {
  const res = await api.put(`/participants/${participantId}`, participantData);
  return res.data;
};

export const removeParticipant = async (participantId, sessionId) => {
  const res = await api.delete(`/participants/${participantId}/session/${sessionId}`);
  return res.data;
};

// Item APIs
export const createItem = async (itemData) => {
  const res = await api.post('/items', itemData);
  return res.data;
};

export const getSessionItems = async (sessionId) => {
  // Use debounce for this frequently called function
  const key = `getSessionItems-${sessionId}`;
  const res = await debounce(key, () => api.get(`/items/session/${sessionId}`), 500);
  return res.data;
};

export const deleteItem = async (itemId) => {
  const res = await api.delete(`/items/${itemId}`);
  return res.data;
};

export const updateItemStatus = async (itemId, status) => {
  const res = await api.put(`/items/${itemId}/status`, { status });
  return res.data;
};

export const setFinalEstimate = async (itemId, finalEstimate) => {
  const res = await api.put(`/items/${itemId}/estimate`, { finalEstimate });
  return res.data;
};

export const submitVote = async (itemId, participantId, value) => {
  // Debounce votes to prevent double-submissions
  const key = `submitVote-${itemId}-${participantId}`;
  const res = await debounce(key, () => 
    api.post(`/items/${itemId}/vote`, { participantId, value })
  );
  return res.data;
};

export const getItemVotes = async (itemId) => {
  // Use debounce for this frequently called function
  const key = `getItemVotes-${itemId}`;
  const res = await debounce(key, () => api.get(`/items/${itemId}/votes`));
  return res.data;
};

export const startNextRound = async (itemId) => {
  const res = await api.post(`/items/${itemId}/nextround`);
  return res.data;
};

export const reorderItems = async (sessionId, items) => {
  const res = await api.put('/items/reorder', { sessionId, items });
  return res.data;
};

// AI Estimation APIs
export const getAISuggestion = async (itemId, itemData) => {
  const res = await api.post(`/items/${itemId}/ai-suggestion`, itemData);
  return res.data;
};

// JIRA API helpers
export const pushEstimateToJira = async (issueKey, estimateData) => {
  // estimateData should contain: { storyPointFieldId, estimate, organizationId }
  const res = await api.post(`/jira/issue/${issueKey}/estimate`, estimateData);
  return res.data;
};

// JIRA Sprint API helpers
export const getJiraBoards = async (projectKey = null, organizationId = null) => {
  const params = {};
  if (projectKey) params.projectKey = projectKey;
  if (organizationId) params.organizationId = organizationId;
  const res = await api.get('/jira/boards', { params });
  return res.data;
};

export const getBoardSprints = async (boardId, state = null, organizationId = null) => {
  const params = {};
  if (state) params.state = state;
  if (organizationId) params.organizationId = organizationId;
  const res = await api.get(`/jira/boards/${boardId}/sprints`, { params });
  return res.data;
};

export const getSprintDetails = async (sprintId, organizationId = null) => {
  const params = organizationId ? { organizationId } : {};
  const res = await api.get(`/jira/sprints/${sprintId}`, { params });
  return res.data;
};

export const getBoardVelocity = async (boardId, sprintCount = 10, organizationId = null) => {
  const params = { sprintCount };
  if (organizationId) params.organizationId = organizationId;
  const res = await api.get(`/jira/boards/${boardId}/velocity`, { params });
  return res.data;
};

export const getSprintComparison = async (boardId, comparisonDate, sprintCount = 20, organizationId = null) => {
  const params = { boardId, comparisonDate, sprintCount };
  if (organizationId) params.organizationId = organizationId;
  const res = await api.get('/jira/sprint-comparison', { params });
  return res.data;
};

export const getJiraEpics = async (projectKey, organizationId = null) => {
  const params = { projectKey };
  if (organizationId) params.organizationId = organizationId;
  const res = await api.get('/jira/epics', { params });
  return res.data;
};

export const getJiraIssue = async (issueKey, organizationId = null) => {
  const params = organizationId ? { organizationId } : {};
  const res = await api.get(`/jira/issue/${issueKey}`, { params });
  return res.data;
};

// JIRA Comment API helpers
export const getJiraComments = async (issueKey, organizationId = null) => {
  const params = organizationId ? { organizationId } : {};
  const res = await api.get(`/jira/issue/${issueKey}/comments`, { params });
  return res.data;
};

export const postJiraComment = async (issueKey, body, organizationId = null) => {
  const payload = { body };
  if (organizationId) payload.organizationId = organizationId;
  const res = await api.post(`/jira/issue/${issueKey}/comment`, payload);
  return res.data;
};

// JIRA Credentials Source helper
export const getJiraCredentialsSource = async (organizationId = null) => {
  const params = organizationId ? { organizationId } : {};
  const res = await api.get('/jira/credentials-source', { params });
  return res.data;
};

const apiMethods = {
  createSession,
  getSession,
  updateSession,
  joinSession,
  updateParticipant,
  removeParticipant,
  createItem,
  getSessionItems,
  deleteItem,
  updateItemStatus,
  setFinalEstimate,
  submitVote,
  getItemVotes,
  startNextRound,
  reorderItems,
  checkServerStatus,
  pushEstimateToJira,
  getJiraBoards,
  getBoardSprints,
  getSprintDetails,
  getBoardVelocity,
  getSprintComparison,
  getJiraEpics,
  getJiraIssue,
  getJiraComments,
  postJiraComment,
  getJiraCredentialsSource,
  getAISuggestion
};

export default apiMethods;